import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight } from "lucide-react";
import { EASE_EXPO } from "@/components/motion/primitives";

const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";

/** Pages with their own main action, where a second sticky button would compete. */
const HIDDEN_ON = ["/blog", "/faq", "/community", "/impressum", "/contact", "/privacy", "/terms", "/cookies"];

/**
 * Sticky "Get visa help" on phones (<768px). Appears once the visitor has
 * scrolled past the first screen and stays out of the way while the plan card
 * itself is on screen and near the footer.
 */
export function StickyMobileCTA() {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const hidden = HIDDEN_ON.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));

  useEffect(() => {
    if (hidden) {
      setShow(false);
      return;
    }
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const nearBottom = scrollY + window.innerHeight > document.body.scrollHeight - 600;
      const card = document.getElementById("plan")?.getBoundingClientRect();
      const cardOnScreen = !!card && card.top < window.innerHeight && card.bottom > 0;
      setShow(scrollY > window.innerHeight * 0.6 && !nearBottom && !cardOnScreen);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hidden, location.pathname]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE_EXPO }}
          className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
        >
          <div className="safe-area-bottom border-t border-[#E0E3DE]/70 bg-[#FBFBF8]/95 px-5 py-3 backdrop-blur-md">
            <Link
              to="/start"
              className="flex w-full items-center justify-center gap-2 rounded-full py-3 text-[16px] font-bold transition-transform active:scale-[0.98]"
              style={{ backgroundColor: TEAL, color: BRAND_DARK, minHeight: "48px" }}
            >
              Get visa help
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
